import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import ContactForm from '../components/ContactForm';
import StarDoctorsIndia from '../components/StarDoctorsIndia';
import StarDoctorsTurkey from '../components/StarDoctorsTurkey';
import TestimonialVideoCarousel from '../components/TestimonialVideoCarousel';
import { useBestDoctors } from '../hooks/useDoctors';
import { useNavigate } from 'react-router-dom';
import { doctorService, hospitalService, treatmentService } from '../services/api';
import { hospitalNameToSlug } from '../utils/hospitalUtils';
import { treatmentTitleToSlug } from '../utils/treatmentUtils';
import { countries } from '../constants/countries';
import ReCAPTCHA from "react-google-recaptcha";

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
};

// Indian Doctors Data
const bestIndianDoctors = [
  {
    name: "Dr Hitesh Garg",
    specialty: "Orthopaedics",
    qualification: "MS Ortho",
    experience: "15",
    image: "/images/image copy 29.png",
    link: "/doctors/dr-hitesh-garg/",
  },
  {
    name: "Dr Randeep Wadhawan",
    specialty: "Cardiology",
    qualification: "DM Cardiology",
    experience: "20",
    image: "/images/image copy 26.png",
    link: "/doctors/dr-randeep-wadhawan/",
  },
  {
    name: "Dr Lohit Reddy",
    specialty: "Oncology",
    qualification: "DM Oncology",
    experience: "18",
    image: "/images/image copy 28.png",
    link: "/doctors/dr-lohit-reddy/",
  },
  {
    name: "Dr Hemant Sharma",
    specialty: "Orthopaedics",
    qualification: "MRCS (England)",
    experience: "26",
    image: "/images/image copy 31.png",
    link: "/doctors/dr-hemant-sharma/",
  },
  {
    name: "Dr IPS Oberoi",
    specialty: "Orthopaedics",
    qualification: "MCh Ortho",
    experience: "25",
    image: "/images/image copy 30.png",
    link: "/doctors/dr-ips-oberoi/",
  },
  {
    name: "Dr Ankur Bahl",
    specialty: "Oncology",
    qualification: "DM Oncology",
    experience: "22",
    image: "/images/image copy 25.png",
    link: "/doctors/dr-ankur-bahl/",
  },
  {
    name: "Dr Kirthi Koushik",
    specialty: "Cardiology",
    qualification: "DM Cardiology",
    experience: "19",
    image: "/images/image copy 27.png",
    link: "/doctors/dr-kirthi-koushik/",
  },
  {
    name: "Dr Atul Peters",
    specialty: "Weight Loss Surgery",
    qualification: "DNB and MAS",
    experience: "27",
    image: "/images/image copy 32.png",
    link: "/doctors/dr-atul-peters/",
  },
  {
    name: "Dr Vivek Vij",
    specialty: "Liver Transplant",
    qualification: "DNB - Surgery",
    experience: "15",
    image: "/images/image copy 25.png",
    link: "/doctors/dr-vivek-vij/",
  },
  {
    name: "Dr Sandeep Vaishya",
    specialty: "Neurosurgery",
    qualification: "MCh (Neurosurgery)",
    experience: "24",
    image: "/images/image copy 26.png",
    link: "/doctors/dr-sandeep-vaishya/",
  },
  {
    name: "Dr Dharma Choudhary",
    specialty: "Hematology",
    qualification: "DM (Clinical Hematology)",
    experience: "20",
    image: "/images/image copy 28.png",
    link: "/doctors/dr-dharma-choudhary/",
  },
  {
    name: "Dr Rana Patir",
    specialty: "Neurosurgery",
    qualification: "MCh (Neurosurgery)",
    experience: "32",
    image: "/images/image copy 27.png",
    link: "/doctors/dr-rana-patir/",
  },
];

// Turkish Doctors Data
const bestTurkishDoctors = [
  {
    name: "Prof Aydın Alper",
    specialty: "Urology",
    qualification: "MS Urology",
    experience: "30",
    image: "/images/image copy 34.png",
    link: "/doctors/prof-aydin-alper/",
  },
  {
    name: "Prof Tijen Alkan Bozkaya",
    specialty: "Cardiology",
    qualification: "MS Cardiology",
    experience: "24",
    image: "/images/image copy 35.png",
    link: "/doctors/prof-tijen-alkan-bozkaya/",
  },
  {
    name: "Prof Dr Remzi Saglam",
    specialty: "Urology",
    qualification: "MS Urology",
    experience: "42",
    image: "/images/image copy 36.png",
    link: "/doctors/prof-dr-remzi-saglam/",
  },
  {
    name: "Op Dr Semih Tiber Mentese",
    specialty: "Cosmetics",
    qualification: "MD Cosmetics",
    experience: "14",
    image: "/images/image copy 37.png",
    link: "/doctors/op-dr-semih-tiber-mentese/",
  },
  {
    name: "Assoc Prof Dr Cagri Guneri",
    specialty: "Urology",
    qualification: "MS Urology",
    experience: "22",
    image: "/images/image copy 38.png",
    link: "/doctors/assoc-prof-dr-cagri-guneri/",
  },
  {
    name: "Prof Dr Savaş Tuna",
    specialty: "Oncology",
    qualification: "MS Oncology",
    experience: "35",
    image: "/images/image copy 39.png",
    link: "/doctors/prof-dr-savas-tuna/",
  },
];

// Treatments Data
const treatmentsData = [
  {
    name: "Colorectal Cancer",
    price: "USD $5500",
    link: "/treatments/colorectal-cancer-treatment/"
  },
  {
    name: "Angioplasty",
    price: "USD $4500",
    link: "/treatments/angioplasty/"
  },
  {
    name: "Hip Replacement Surgery",
    price: "USD $4200",
    link: "/treatments/hip-replacement-surgery/"
  },
  {
    name: "Spinal Spondylitis",
    price: "USD $1500",
    link: "/treatments/spinal-spondylitis/"
  },
];

const Doctors = () => {
  const [currentTreatmentIndex, setCurrentTreatmentIndex] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '',
    mobile: '',
    email: '',
    preferredLanguage: '',
    country: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcyZ50rAAAAALJS9q7T5m_U115V1UYbM6b20PjA';
  
  // New search functionality from home page
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [homeSearchResults, setHomeSearchResults] = useState({ doctors: [], hospitals: [], treatments: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = React.useRef(null);
  
  // Fetch doctors data dynamically
  const { doctors: bestDoctors, loading, error } = useBestDoctors();

  // Search ranking utility functions
  const calculateRelevanceScore = (item, query, type) => {
    const lowerQuery = query.toLowerCase().trim();
    let score = 0;
    
    // Primary fields for ranking (starting character matches get highest priority)
    let primaryField = '';
    if (type === 'doctor') {
      primaryField = item.name?.toLowerCase() || '';
    } else if (type === 'hospital') {
      primaryField = item.name?.toLowerCase() || '';
    } else if (type === 'treatment') {
      primaryField = item.title?.toLowerCase() || '';
    }
    
    // Highest priority: Exact match at start of primary field
    if (primaryField.startsWith(lowerQuery)) {
      score += 1000;
    }
    // High priority: Primary field contains query at word boundary
    else if (primaryField.includes(' ' + lowerQuery) || primaryField.includes('-' + lowerQuery)) {
      score += 800;
    }
    // Medium-high priority: Primary field contains query anywhere
    else if (primaryField.includes(lowerQuery)) {
      score += 600;
    }
    
    // Secondary fields for additional scoring
    const secondaryFields = [];
    if (type === 'doctor') {
      secondaryFields.push(
        item.specialty?.toLowerCase() || '',
        item.hospital?.toLowerCase() || '',
        item.description?.toLowerCase() || ''
      );
    } else if (type === 'hospital') {
      secondaryFields.push(
        item.country?.toLowerCase() || '',
        ...(item.specialities?.map(s => s.toLowerCase()) || []),
        item.description?.toLowerCase() || ''
      );
    } else if (type === 'treatment') {
      secondaryFields.push(
        ...(item.associatedSpecialities?.map(s => s.toLowerCase()) || []),
        item.description?.toLowerCase() || '',
        item.shortDescription?.toLowerCase() || ''
      );
    }
    
    // Check secondary fields for matches
    secondaryFields.forEach(field => {
      if (field.startsWith(lowerQuery)) {
        score += 400;
      } else if (field.includes(' ' + lowerQuery) || field.includes('-' + lowerQuery)) {
        score += 200;
      } else if (field.includes(lowerQuery)) {
        score += 100;
      }
    });
    
    // Bonus for shorter field names (more specific matches)
    if (primaryField.length > 0 && primaryField.includes(lowerQuery)) {
      score += Math.max(0, 100 - primaryField.length);
    }
    
    return score;
  };

  const rankSearchResults = (results, query) => {
    const rankedResults = {
      doctors: [...results.doctors],
      hospitals: [...results.hospitals],
      treatments: [...results.treatments]
    };
    
    // Rank each category
    rankedResults.doctors = rankedResults.doctors
      .map(doctor => ({
        ...doctor,
        relevanceScore: calculateRelevanceScore(doctor, query, 'doctor')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    rankedResults.hospitals = rankedResults.hospitals
      .map(hospital => ({
        ...hospital,
        relevanceScore: calculateRelevanceScore(hospital, query, 'hospital')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    rankedResults.treatments = rankedResults.treatments
      .map(treatment => ({
        ...treatment,
        relevanceScore: calculateRelevanceScore(treatment, query, 'treatment')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    return rankedResults;
  };

  // Function to get ordered sections based on highest relevance scores
  const getOrderedSections = (searchResults) => {
    const sections = [];
    
    // Calculate max score for each category
    const maxScores = {
      doctors: Math.max(...searchResults.doctors.map(d => d.relevanceScore || 0), 0),
      hospitals: Math.max(...searchResults.hospitals.map(h => h.relevanceScore || 0), 0),
      treatments: Math.max(...searchResults.treatments.map(t => t.relevanceScore || 0), 0)
    };
    
    // Create section objects with their max scores
    if (searchResults.doctors.length > 0) {
      sections.push({ type: 'doctors', maxScore: maxScores.doctors, data: searchResults.doctors });
    }
    if (searchResults.hospitals.length > 0) {
      sections.push({ type: 'hospitals', maxScore: maxScores.hospitals, data: searchResults.hospitals });
    }
    if (searchResults.treatments.length > 0) {
      sections.push({ type: 'treatments', maxScore: maxScores.treatments, data: searchResults.treatments });
    }
    
    // Sort by highest max score first
    return sections.sort((a, b) => b.maxScore - a.maxScore);
  };

  // Function to render a search result section
  const renderSearchSection = (section) => {
    const { type, data } = section;
    
    if (type === 'doctors') {
      return (
        <div key="doctors" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100">
            Doctors
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((doctor) => (
            <div
              key={`doctor-${doctor.id}`}
              onClick={() => handleResultClick('doctor', doctor)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={doctor.image || '/images/default-doctor.jpg'}
                alt={doctor.name}
                className="w-12 h-12 rounded-full object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{doctor.name}</h4>
                <p className="text-sm text-gray-600">{doctor.specialty}</p>
                <p className="text-xs text-blue-600">{doctor.hospital}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (type === 'hospitals') {
      return (
        <div key="hospitals" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100">
            Hospitals
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((hospital) => (
            <div
              key={`hospital-${hospital.id}`}
              onClick={() => handleResultClick('hospital', hospital)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={hospital.mainImage || '/images/default-hospital.jpg'}
                alt={hospital.name}
                className="w-12 h-12 rounded-lg object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{hospital.name}</h4>
                <p className="text-sm text-gray-600">{hospital.country}</p>
                <p className="text-xs text-blue-600">{hospital.specialities?.join(', ')}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (type === 'treatments') {
      return (
        <div key="treatments" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100">
            Treatments
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((treatment) => (
            <div
              key={`treatment-${treatment.id || treatment._id}`}
              onClick={() => handleResultClick('treatment', treatment)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={treatment.headerImage || '/images/default-treatment.jpg'}
                alt={treatment.title}
                className="w-12 h-12 rounded-lg object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{treatment.title}</h4>
                <p className="text-sm text-gray-600">{treatment.associatedSpecialities?.slice(0, 2)?.join(', ')}</p>
                <p className="text-xs text-blue-600">
                  {treatment.costInfo?.india ? `Starting from $${treatment.costInfo.india.minCost}` : 'Cost info available'}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };

  // Debounced search function from home page
  const performSearch = React.useCallback(
    debounce(async (query) => {
      if (query.trim().length < 2) {
        setHomeSearchResults({ doctors: [], hospitals: [], treatments: [] });
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        // Search doctors, hospitals, and treatments in parallel with increased limits for better ranking
        const [doctorsResponse, hospitalsResponse, treatmentsResponse] = await Promise.all([
          doctorService.getAll({ search: query, limit: 20 }),
          hospitalService.getAll({ search: query, limit: 20 }),
          treatmentService.getAll({ search: query, limit: 20, status: 'published' })
        ]);

        const rawResults = {
          doctors: doctorsResponse.data?.doctors || [],
          hospitals: hospitalsResponse.data?.hospitals || [],
          treatments: treatmentsResponse.data?.treatments || []
        };

        // Apply ranking system
        const rankedResults = rankSearchResults(rawResults, query);

        setHomeSearchResults(rankedResults);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setHomeSearchResults({ doctors: [], hospitals: [], treatments: [] });
        setShowResults(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  // Handle clicking on a search result
  const handleResultClick = (type, item) => {
    setShowResults(false);
    setSearchQuery('');
    
    if (type === 'doctor') {
      navigate(`/doctor-details/${item.id}`);
    } else if (type === 'hospital') {
      navigate(`/hospital/${hospitalNameToSlug(item.name)}`);
    } else if (type === 'treatment') {
      navigate(`/treatments/${item.slug || treatmentTitleToSlug(item.title)}`);
    }
  };

  // Handle search button click
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to a more comprehensive search results page
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
      setSearchQuery('');
    }
  };

  // Close search results when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounced function
  React.useEffect(() => {
    return () => {
      performSearch.cancel();
    };
  }, [performSearch]);

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate reCAPTCHA
    if (!recaptchaValue) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://medical-linkers.onrender.com/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          formType: 'doctor-consultation',
          recaptchaToken: recaptchaValue
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ type: 'success', message: 'Thank you! Your consultation request has been sent successfully.' });
        setFormData({
          name: '',
          countryCode: '',
          mobile: '',
          email: '',
          preferredLanguage: '',
          country: '',
          city: '',
          message: ''
        });
        setRecaptchaValue(null);
      } else {
        setSubmitStatus({ type: 'error', message: data.message || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Treatment slider controls
  const nextTreatmentSlide = () => {
    setCurrentTreatmentIndex(prev => (prev + 1) % Math.ceil(treatmentsData.length / 4));
  };

  const prevTreatmentSlide = () => {
    setCurrentTreatmentIndex(prev => prev === 0 ? Math.ceil(treatmentsData.length / 4) - 1 : prev - 1);
  };

  const getTreatmentsToShow = (treatments, currentIndex) => {
    const itemsPerSlide = 4;
    const start = currentIndex * itemsPerSlide;
    return treatments.slice(start, start + itemsPerSlide);
  };

  return (
    <div>
      <Header />
      
      {/* Main Doctor Search Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-8">
                <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                  <span className="text-[#1976f2] font-extrabold">Top doctors of the world</span>, at one place
                </h1>
              </div>
              
              {/* New Search Box */}
              <div className="relative mb-8" ref={searchRef}>
                <form onSubmit={handleSearchSubmit}>
                  <input 
                    type="text" 
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search for Hospitals/Doctors/Specialisation"
                    className="w-full py-3 px-4 rounded-full border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button 
                    type="submit"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-[#1976f2] text-white p-2 rounded-full hover:bg-blue-600 transition-colors"
                  >
                    {isSearching ? (
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  </button>
                </form>

                {/* Search Results Dropdown */}
                {showResults && (homeSearchResults.doctors.length > 0 || homeSearchResults.hospitals.length > 0 || homeSearchResults.treatments.length > 0) && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-50 max-h-96 overflow-y-auto">
                    {/* Render sections in order of relevance */}
                    {getOrderedSections(homeSearchResults).map(section => renderSearchSection(section))}

                    {/* View All Results */}
                    <div className="p-3 border-t border-gray-100">
                      <button
                        onClick={handleSearchSubmit}
                        className="w-full text-center text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View all results for "{searchQuery}"
                      </button>
                    </div>
                  </div>
                )}

                {/* No Results Message */}
                {showResults && homeSearchResults.doctors.length === 0 && homeSearchResults.hospitals.length === 0 && homeSearchResults.treatments.length === 0 && !isSearching && searchQuery.length >= 2 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-50">
                    <div className="p-4 text-center text-gray-500">
                      No results found for "{searchQuery}"
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <img 
                src="https://medicallinkers.org/img/doctors/d-main-hero.webp" 
                alt="Doctors Hero" 
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Star Doctors of India Section */}
      <StarDoctorsIndia />

      {/* Star Doctors of Turkey Section */}
      <StarDoctorsTurkey />

      {/* Contact Form Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="mb-8">
                <h2 className="text-2xl font-semibold mb-2">Get treated at preferred hospital</h2>
                <p className="text-gray-600">Start your treatment journey now</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {submitStatus && (
                  <div className={`p-3 rounded-lg font-open-sans ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-100 text-green-700 border border-green-300' 
                      : 'bg-red-100 text-red-700 border border-red-300'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}
                
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <select 
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code + country.name} value={country.code}>
                          {country.flag} {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Mobile Number"
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                    />
                  </div>
                </div>
                
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    name="preferredLanguage"
                    value={formData.preferredLanguage}
                    onChange={handleChange}
                    placeholder="Your Preferred Language"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Country"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="City"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                    />
                  </div>
                </div>
                
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe Your Questions"
                    required
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1976f2]"
                  ></textarea>
                </div>
                
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-center">
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaValue(null)}
                      theme="light"
                    />
                  </div>
                  {!recaptchaValue && submitStatus?.type === 'error' && submitStatus.message.includes('reCAPTCHA') && (
                    <p className="text-red-500 text-sm text-center font-open-sans">Please complete the reCAPTCHA verification</p>
                  )}
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#1976f2] text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
            
            <div>
              <img 
                src="https://medicallinkers.org/img/doctors/doctors-form.webp" 
                alt="Doctors Form" 
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Guide Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold">
              <span className="text-[#1976f2]">Your Treatment</span> Guide
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition h-full flex flex-col">
                <div className="h-48 mb-4 overflow-hidden rounded">
                  <img 
                    src="/images/tg1.webp" 
                    alt="Send inquiry" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-[#1976f2] text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h5 className="font-semibold text-gray-800 flex-grow flex items-center justify-center">Send an inquiry with your reports</h5>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition h-full flex flex-col">
                <div className="h-48 mb-4 overflow-hidden rounded">
                  <img 
                    src="/images/tg2.webp" 
                    alt="Select treatment" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-[#1976f2] text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h5 className="font-semibold text-gray-800 flex-grow flex items-center justify-center">Select from multiple treatment options</h5>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition h-full flex flex-col">
                <div className="h-48 mb-4 overflow-hidden rounded">
                  <img 
                    src="/images/tg3.webp" 
                    alt="Confirm travel" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-[#1976f2] text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h5 className="font-semibold text-gray-800 flex-grow flex items-center justify-center">Confirm travel with our travel assistant</h5>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition h-full flex flex-col">
                <div className="h-48 mb-4 overflow-hidden rounded">
                  <img 
                    src="/images/tg4.webp" 
                    alt="Receive treatment" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-[#1976f2] text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h5 className="font-semibold text-gray-800 flex-grow flex items-center justify-center">Receive treatment & care</h5>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments Available Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold">
              <span className="text-[#1976f2]">Treatments</span> Available
            </h3>
            <p className="mt-3 text-gray-600">Choose from 100+ globally 5-star rated treatments</p>
          </div>

          <div className="relative">
            {/* Navigation Buttons */}
            <button 
              onClick={prevTreatmentSlide}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              onClick={nextTreatmentSlide}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Treatments Slider */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${currentTreatmentIndex * 100}%)` }}
              >
                {Array.from({ length: Math.ceil(treatmentsData.length / 4) }).map((_, slideIndex) => (
                  <div key={slideIndex} className="w-full flex-shrink-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {getTreatmentsToShow(treatmentsData, slideIndex).map((treatment, index) => (
                        <div key={index} className="bg-gradient-to-br from-blue-50 to-white rounded-lg shadow-md hover:shadow-lg transition p-6 text-center">
                          <h4 className="text-lg font-semibold mb-3 text-gray-800">{treatment.name}</h4>
                          <h3 className="text-xl font-bold text-gray-700 mb-2">
                            Starts from
                            <span className="block text-2xl text-[#1976f2] mt-1">{treatment.price}</span>
                          </h3>
                          <p className="text-gray-600 mb-4">Expert doctors | Assured Care</p>
                          <a 
                            href={treatment.link} 
                            className="bg-[#1976f2] text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition inline-flex items-center"
                          >
                            Enquire More
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Videos Section */}
      <TestimonialVideoCarousel 
        title="Doctor Testimonials & Success Stories"
        subtitle="Hear from leading doctors and their patients about successful treatments"
        limit={6}
        category="Doctor Interviews"
      />

      {/* Doctor Consultation Form Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center bg-white rounded-lg overflow-hidden shadow-lg">
            <div className="md:w-1/2 p-8">
              <h2 className="text-3xl font-bold mb-2">
                Consult with our Expert Doctors
              </h2>
              <p className="text-gray-600 mb-6">
                Get personalized medical advice from world-class doctors
              </p>

              {/* Status Message */}
              {submitStatus && (
                <div className={`p-3 rounded-lg mb-4 font-open-sans ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-100 text-green-700 border border-green-300' 
                    : 'bg-red-100 text-red-700 border border-red-300'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <select 
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded text-gray-500"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code + country.name} value={country.code}>
                          {country.flag} {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Mobile Number"
                      required
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>

                <div>
                  <input
                    type="text"
                    name="preferredLanguage"
                    value={formData.preferredLanguage}
                    onChange={handleChange}
                    placeholder="Your Preferred Language"
                    className="w-full p-3 border border-gray-300 rounded"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Country"
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="City"
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe your medical concerns"
                    required
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded"
                  ></textarea>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-center">
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaValue(null)}
                      theme="light"
                    />
                  </div>
                  {!recaptchaValue && submitStatus?.type === 'error' && submitStatus.message.includes('reCAPTCHA') && (
                    <p className="text-red-500 text-sm text-center font-open-sans">Please complete the reCAPTCHA verification</p>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#1976f2] text-white px-8 py-3 rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Book Consultation'}
                  </button>
                </div>
              </form>
            </div>

            <div className="md:w-1/2">
              <img
                src="/images/image copy 22.png"
                alt="Medical Professional"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="pt-16 pb-0 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="bg-[#4285f4] rounded-3xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h4 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                  Get your treatment done by the best doctors of the world.
                </h4>
              </div>
              <div className="text-center lg:text-right">
                <p className="text-white text-lg mb-6 font-medium">Get in touch with an Expert Advisor</p>
                <a 
                  href="https://wa.me/9599222655" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold transition inline-flex items-center text-lg shadow-lg hover:shadow-xl"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  Chat Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingChatButton />
    </div>
  );
};

export default Doctors;

